import { readInt32LE, readUInt16LE, readUInt32LE, readUInt8 } from "../buffer-io";
import { isZlibWrapped, loadTelemetryBuffer } from "./io";
import { parseChsPayload } from "./chs";
import { decodeChannelSample } from "./decode";
import { decodeGpsTrack } from "./gps";
import { aimLogDateTimeToMs, parseSessionMetadata } from "./metadata";
import type {
  ChannelDefinition,
  ChannelSample,
  LapMarker,
  ParseOptions,
  ParsedSession,
} from "./types";

type ParseContext = {
  buffer: Buffer;
  channels: Map<number, ChannelDefinition>;
  laps: LapMarker[];
  gpsPayloads: Buffer[];
  seriesByIndex: Map<number, ChannelSample[]>;
  stats: ParsedSession["stats"];
  options: ParseOptions;
};

export function parseXrkBuffer(buffer: Buffer, options: ParseOptions = {}): ParsedSession {
  const decoded = isZlibWrapped(buffer) ? loadTelemetryBuffer(buffer) : buffer;
  const context: ParseContext = {
    buffer: decoded,
    channels: new Map<number, ChannelDefinition>(),
    laps: [],
    gpsPayloads: [],
    seriesByIndex: new Map<number, ChannelSample[]>(),
    stats: { singleSamples: 0, multiSamples: 0, groupSamples: 0, gpsSamples: 0, skippedBytes: 0 },
    options,
  };

  scanRegion(context, 0, decoded.length);

  const channelList = [...context.channels.values()].sort((a, b) => a.index - b.index);
  const gps = context.options.headersOnly
    ? []
    : decodeGpsTrack(context.gpsPayloads, context.options.maxGpsSamples);
  context.stats.gpsSamples = gps.length;

  const metadata = parseSessionMetadata(decoded);
  const recordedAtMs = metadata.logDate ? aimLogDateTimeToMs(metadata.logDate, metadata.logTime) : null;

  return {
    format: isZlibWrapped(buffer) ? "xrz" : "xrk",
    compressedBytes: buffer.length,
    decodedBytes: decoded.length,
    channels: channelList,
    laps: context.laps,
    gps,
    seriesByIndex: context.seriesByIndex,
    metadata,
    stats: {
      ...context.stats,
      ...(recordedAtMs ? { recordedAtMs } : {}),
    },
  };
}

function scanRegion(context: ParseContext, start: number, end: number): void {
  let offset = start;

  while (offset < end) {
    const header = tryParseHeaderMessage(context.buffer, offset, end);
    if (header) {
      if (header.token === "CHS") {
        const channel = parseChsPayload(header.payload);
        context.channels.set(channel.index, channel);
      } else if (header.token === "GPS" || header.token === "GPS1") {
        if (header.payload.length === 56) {
          context.gpsPayloads.push(Buffer.from(header.payload));
        }
      } else if (header.token === "LAP") {
        context.laps.push(parseLapPayload(header.payload));
      } else if (header.token === "CNF" || header.token === "ENF") {
        const savedBuffer = context.buffer;
        context.buffer = header.payload;
        scanRegion(context, 0, header.payload.length);
        context.buffer = savedBuffer;
      }
      offset += header.frameBytes;
      continue;
    }

    if (offset + 2 <= end && readUInt8(context.buffer, offset) === 0x28) {
      const markerByte = readUInt8(context.buffer, offset + 1);
      if (markerByte === 0x53) {
        const parsed = tryParseSingleSample(context, offset, end);
        if (parsed) {
          if (!context.options.headersOnly) {
            context.stats.singleSamples += 1;
          }
          offset += parsed;
          continue;
        }
      }
      if (markerByte === 0x4d) {
        const parsed = tryParseMultiSample(context, offset, end);
        if (parsed) {
          if (!context.options.headersOnly) {
            context.stats.multiSamples += 1;
          }
          offset += parsed;
          continue;
        }
      }
    }

    context.stats.skippedBytes += 1;
    offset += 1;
  }
}

export function readTelemetryFromBuffer(buffer: Buffer, options: ParseOptions = {}): ParsedSession {
  return parseXrkBuffer(buffer, options);
}

export function getChannelSeries(session: ParsedSession, selector: number | string): ChannelSample[] {
  if (typeof selector === "number") {
    return session.seriesByIndex.get(selector) ?? [];
  }

  const channel = session.channels.find((entry) => entry.shortName === selector);
  if (!channel) {
    return [];
  }
  return session.seriesByIndex.get(channel.index) ?? [];
}

export function validateParsedSession(session: ParsedSession): string | null {
  if (session.channels.some((channel) => channel.shortName.includes(","))) {
    return "Channel names look corrupt — reload the app and parse again.";
  }

  if (session.seriesByIndex.size === 0) {
    return "No channel sample data was parsed.";
  }

  const sampleCount = session.stats.singleSamples + session.stats.multiSamples;
  if (sampleCount === 0) {
    return "No telemetry samples found in file.";
  }

  const hasUsableGps = session.gps.length >= 100;
  const hasRichSamples = sampleCount >= 1_000;
  if (hasUsableGps && hasRichSamples) {
    return null;
  }

  if (session.stats.skippedBytes > Math.max(session.decodedBytes * 0.01, 50_000)) {
    return `Parse skipped ${session.stats.skippedBytes.toLocaleString()} bytes — file may be corrupt.`;
  }

  return null;
}

export function validateSessionJsonExport(json: Record<string, unknown>): string | null {
  const channels = Array.isArray(json.channels) ? json.channels : [];
  if (
    channels.some(
      (channel) =>
        typeof channel === "object" &&
        channel !== null &&
        typeof (channel as { shortName?: unknown }).shortName === "string" &&
        (channel as { shortName: string }).shortName.includes(","),
    )
  ) {
    return "Channel metadata looks corrupt — reload the app and parse again.";
  }

  const series = json.series;
  if (!series || typeof series !== "object" || Array.isArray(series)) {
    return "No series object in JSON export.";
  }

  const hasSampleData = Object.values(series as Record<string, unknown>).some(
    (entry) => Array.isArray(entry) && entry.length > 0,
  );
  if (!hasSampleData) {
    return "No channel sample data in JSON export.";
  }

  const stats = json.stats;
  if (stats && typeof stats === "object" && !Array.isArray(stats)) {
    const singleSamples = Number((stats as { singleSamples?: unknown }).singleSamples) || 0;
    const multiSamples = Number((stats as { multiSamples?: unknown }).multiSamples) || 0;
    if (singleSamples + multiSamples === 0) {
      return "No telemetry samples found in JSON export.";
    }
  }

  return null;
}

export function sessionToJson(session: ParsedSession, options: ParseOptions = {}): Record<string, unknown> {
  const retain = buildRetainSet(session.channels, options);
  const series: Record<string, ChannelSample[]> = {};
  const exportedChannels = session.channels.filter((channel) => !retain || retain.has(channel.index));
  const usedSeriesKeys = new Set<string>();

  for (const channel of exportedChannels) {
    const key = allocateSeriesKey(channel, usedSeriesKeys);
    series[key] = session.seriesByIndex.get(channel.index) ?? [];
  }

  for (const [index, samples] of session.seriesByIndex.entries()) {
    if (retain && !retain.has(index)) {
      continue;
    }
    if (exportedChannels.some((channel) => channel.index === index)) {
      continue;
    }
    const fallback = `ch_${index}`;
    if (!usedSeriesKeys.has(fallback)) {
      usedSeriesKeys.add(fallback);
      series[fallback] = samples;
    }
  }

  return {
    format: session.format,
    compressedBytes: session.compressedBytes,
    decodedBytes: session.decodedBytes,
    metadata: session.metadata,
    channels: exportedChannels
      .map((channel) => ({
        index: channel.index,
        shortName: channel.shortName,
        longName: channel.longName,
        unit: channel.unit,
        decimals: channel.decimals,
        decoderType: channel.decoderType,
        dataSize: channel.dataSize,
        samplePeriodMs: channel.samplePeriodMs,
      })),
    laps: session.laps,
    stats: session.stats,
    gps: session.gps
      .slice(0, options.maxGpsSamples ?? session.gps.length)
      .map((sample) => ({
      timecodeMs: sample.timecodeMs,
      lat: sample.lat,
      lon: sample.lon,
      alt: sample.alt,
      speed: sample.speed,
      fix: sample.fix,
      satellites: sample.satellites,
    })),
    series,
  };
}

function allocateSeriesKey(channel: ChannelDefinition, usedSeriesKeys: Set<string>): string {
  const base = channel.shortName.trim();
  if (base && !usedSeriesKeys.has(base)) {
    usedSeriesKeys.add(base);
    return base;
  }

  const fallback = `ch_${channel.index}`;
  usedSeriesKeys.add(fallback);
  return fallback;
}

function buildRetainSet(channels: ChannelDefinition[], options: ParseOptions): Set<number> | null {
  if (!options.channelIndices?.length && !options.channelNames?.length) {
    return null;
  }

  const retain = new Set<number>();
  for (const index of options.channelIndices ?? []) {
    retain.add(index);
  }
  for (const name of options.channelNames ?? []) {
    const channel = channels.find((entry) => entry.shortName === name);
    if (channel) {
      retain.add(channel.index);
    }
  }
  return retain;
}

function tryParseHeaderMessage(
  buffer: Buffer,
  offset: number,
  end = buffer.length,
): { token: string; payload: Buffer; frameBytes: number } | null {
  if (offset + 12 > end) {
    return null;
  }

  const payloadLength = readInt32LE(buffer, offset + 6);
  if (payloadLength < 0 || payloadLength > 10_000_000) {
    return null;
  }
  if (readUInt8(buffer, offset + 11) !== 0x3e) {
    return null;
  }

  const payloadStart = offset + 12;
  const payloadEnd = payloadStart + payloadLength;
  const frameEnd = payloadEnd + 8;
  const frameBytes = frameEnd - offset;
  if (payloadEnd > end || frameEnd > end) {
    return null;
  }

  if (readUInt8(buffer, payloadEnd) !== 0x3c) {
    return null;
  }
  if (readUInt8(buffer, payloadEnd + 7) !== 0x3e) {
    return null;
  }

  const wireToken = readUInt32LE(buffer, offset + 2);
  const footerToken = readUInt32LE(buffer, payloadEnd + 1);
  if (wireToken !== footerToken) {
    return null;
  }

  const payload = buffer.subarray(payloadStart, payloadEnd);
  const checksum = readUInt16LE(buffer, payloadEnd + 5);
  if (checksum !== (payloadChecksum(payload) & 0xffff)) {
    return null;
  }

  return {
    token: tokenToString(wireToken),
    payload,
    frameBytes,
  };
}

function payloadChecksum(payload: Buffer): number {
  let sum = 0;
  for (const byte of payload) {
    sum += byte;
  }
  return sum;
}

function tokenToString(token: number): string {
  const bytes = [token & 0xff, (token >> 8) & 0xff, (token >> 16) & 0xff, (token >> 24) & 0xff];
  let end = bytes.length;
  while (end > 0 && bytes[end - 1] === 0) {
    end -= 1;
  }
  if (end === 4 && bytes[3] === 0x20) {
    end = 3;
  }
  return String.fromCharCode(...bytes.slice(0, end));
}

function parseLapPayload(payload: Buffer): LapMarker {
  return {
    segment: readUInt8(payload, 1),
    lapNumber: readUInt16LE(payload, 2),
    durationMs: readUInt32LE(payload, 4),
    endTimeMs: readUInt32LE(payload, 16),
  };
}

function tryParseSingleSample(context: ParseContext, offset: number, end: number): number | null {
  const buffer = context.buffer;
  if (offset + 8 > end) {
    return null;
  }

  const timecodeMs = readInt32LE(buffer, offset + 2);
  const channelIndex = readUInt16LE(buffer, offset + 6);
  const channel = context.channels.get(channelIndex);
  if (!channel || channel.dataSize <= 0) {
    return null;
  }

  const dataStart = offset + 8;
  const dataEnd = dataStart + channel.dataSize;
  const frameEnd = dataEnd + 1;
  const frameBytes = frameEnd - offset;
  if (frameEnd > end || readUInt8(buffer, dataEnd) !== 0x29) {
    return null;
  }

  if (!shouldRetainChannel(channel, context.options) || context.options.headersOnly) {
    return frameBytes;
  }
  if (isChannelFull(channelIndex, context.options, context.seriesByIndex)) {
    return frameBytes;
  }

  const value = decodeChannelSample(channel, buffer.subarray(dataStart, dataEnd));
  appendSample(context.seriesByIndex, channelIndex, { timecodeMs, value });
  return frameBytes;
}

function tryParseMultiSample(context: ParseContext, offset: number, end: number): number | null {
  const buffer = context.buffer;
  if (offset + 10 > end) {
    return null;
  }

  const timecodeMs = readInt32LE(buffer, offset + 2);
  const channelIndex = readUInt16LE(buffer, offset + 6);
  const count = readUInt16LE(buffer, offset + 8);
  const channel = context.channels.get(channelIndex);
  if (!channel || channel.dataSize <= 0 || count <= 0 || count > 10_000) {
    return null;
  }

  const totalDataBytes = channel.dataSize * count;
  const dataStart = offset + 10;
  const dataEnd = dataStart + totalDataBytes;
  const frameEnd = dataEnd + 1;
  const frameBytes = frameEnd - offset;
  if (frameEnd > end || readUInt8(buffer, dataEnd) !== 0x29) {
    return null;
  }

  if (!shouldRetainChannel(channel, context.options) || context.options.headersOnly) {
    return frameBytes;
  }

  const raw = buffer.subarray(dataStart, dataEnd);
  for (let sampleIndex = 0; sampleIndex < count; sampleIndex += 1) {
    if (isChannelFull(channelIndex, context.options, context.seriesByIndex)) {
      break;
    }
    const sliceStart = sampleIndex * channel.dataSize;
    const value = decodeChannelSample(channel, raw.subarray(sliceStart, sliceStart + channel.dataSize));
    appendSample(context.seriesByIndex, channelIndex, {
      timecodeMs: timecodeMs + sampleIndex * channel.samplePeriodMs,
      value,
    });
  }

  return frameBytes;
}

function shouldRetainChannel(channel: ChannelDefinition, options: ParseOptions): boolean {
  if (options.channelIndices?.length) {
    return options.channelIndices.includes(channel.index);
  }
  if (options.channelNames?.length) {
    return options.channelNames.includes(channel.shortName);
  }
  return true;
}

function isChannelFull(
  channelIndex: number,
  options: ParseOptions,
  seriesByIndex: Map<number, ChannelSample[]>,
): boolean {
  if (!options.maxSamplesPerChannel) {
    return false;
  }
  return (seriesByIndex.get(channelIndex)?.length ?? 0) >= options.maxSamplesPerChannel;
}

function appendSample(
  seriesByIndex: Map<number, ChannelSample[]>,
  channelIndex: number,
  sample: ChannelSample,
): void {
  const bucket = seriesByIndex.get(channelIndex);
  if (bucket) {
    bucket.push(sample);
    return;
  }
  seriesByIndex.set(channelIndex, [sample]);
}
